import { a, useSpring } from '@react-spring/three';
import { THREE } from 'expo-three';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  imageUrl: string;
  size: number;
  bmpSize: number;
  showInstantly?: boolean;
  transparencyColor?: string;
  onImageLoad?: () => void;
  opacity?: number;
  animatedIn?: boolean;
};

export function ImageMaterialZoomAble({
  imageUrl,
  size,
  bmpSize,
  showInstantly,
  transparencyColor,
  onImageLoad,
  opacity = 1,
  animatedIn = false,
}: Props) {
  const { dScale: scale } = useAnimatedStates(animatedIn);
  const [loaded, setLoaded] = useState(false);

  const bmpRatio = bmpSize / size;

  const colorMap = useMemo(() => {
    if (!imageUrl || !size) {
      return undefined;
    }

    // black magic on centering a bitmap on a card
    // and rendering its size up to the edges

    const texture = new THREE.TextureLoader().load(imageUrl, () => {
      setLoaded(true);
    });
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.offset.set(0.5, 0.5);
    texture.repeat.set(bmpRatio / bmpSize, bmpRatio / bmpSize);

    return texture;
  }, [imageUrl, size]);

  useEffect(() => {
    if (loaded) {
      onImageLoad?.();
    }
  }, [loaded]);

  colorMap?.repeat.set(
    (bmpRatio / bmpSize) * scale.get(),
    (bmpRatio / bmpSize) * scale.get()
  );

  const getOpacity = () => {
    const internal = (() => {
      if (showInstantly) {
        return 1;
      }

      return loaded ? 1 : 0;
    })();

    return Math.min(internal, opacity);
  };

  const { opacityAnim } = useSpring({
    from: { opacityAnim: 0 },
    to: { opacityAnim: getOpacity() },
    config: { duration: 500 },
  });

  return (
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore
    <a.meshBasicMaterial
      map={colorMap}
      transparent
      opacity={opacityAnim}
      color={transparencyColor}
    />
  );
}

const useAnimatedStates = (animatedIn: boolean) =>
  useSpring({
    from: {
      dScale: animatedIn ? 1.05 : 1,
    },
    to: {
      dScale: animatedIn ? 1 : 1.05,
    },
    config: { duration: 500 },
  });
